'use es6';

import { useContext } from 'react';
import PropTypes from 'prop-types';
import toJS from 'transmute/toJS';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Wizard, WizardStep } from 'foundations-components/transitional/components/Wizard';
import { ModalPanel } from 'foundations-components/transitional/components/ModalPanel';
import { useHideDialog } from '../../../dialog/ducks';
import { fromState } from '../../../permissions/permission-payload';
import { SpecificPartitioningControl } from '../../shared/permissionSettings/SpecificPartitioningControl';
import { PermissionSettingsContext } from '../../shared/permissionSettings/context/PermissionSettingsContext';
import { AssetTypePropType, ASSET_TYPES, ASSIGNMENT_MODES } from '../../shared/permissionSettings/constants';
import AssignmentModeControl from '../../shared/permissionSettings/AssignmentModeControl';
import { ActionStatusesPropType } from '../../shared/actionStatuses';
import ActionSubmitButton from '../../shared/ActionSubmitButton';
import { useTracker } from '../../../utils/hooks/useTracker';
import { EVENTS } from '../../../utils/usageTracker';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const ManageAccessDialog = ({
  actionStatus,
  appUsageTracker,
  assetCount = 1,
  assetName,
  assetSpecificTrackingProps = {},
  assetType,
  onConfirm
}) => {
  const hideDialog = useHideDialog();
  const usageTracker = useTracker(appUsageTracker);
  const context = useContext(PermissionSettingsContext);
  const {
    assignmentMode
  } = context;
  const isDashboardType = assetType === ASSET_TYPES.DASHBOARD;
  const selectedAssetsHeader = /*#__PURE__*/_jsx("h4", {
    children: assetCount > 1 ? /*#__PURE__*/_jsx(FormattedMessage, {
      message: isDashboardType ? 'reporting-action-components.actions.selectedCount.dashboard' : 'reporting-action-components.actions.selectedCount.report',
      options: {
        count: assetCount
      }
    }) : assetName
  });
  return /*#__PURE__*/_jsx(ModalPanel, {
    width: 600,
    "data-test-id": "manage-access-modal",
    children: /*#__PURE__*/_jsxs(Wizard, {
      defaultDoneButton: wizardProps => /*#__PURE__*/_jsx(ActionSubmitButton, Object.assign({
        actionStatus: actionStatus,
        "data-test-id": "manage-access-confirm"
      }, wizardProps, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "reporting-action-components.actions.manageReportAccess.dialog.save"
        })
      })),
      defaultHeading: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "reporting-action-components.actions.manageReportAccess.dialog.title"
      }),
      onReject: () => {
        if (usageTracker) {
          usageTracker.track(EVENTS.editAssetAccessInteraction, Object.assign({
            actionType: 'edit',
            action: 'edit-access',
            assetType: isDashboardType ? 'dashboard' : 'report',
            step: 'exit',
            count: assetCount
          }, assetSpecificTrackingProps));
        }
        hideDialog();
      },
      onConfirm: () => {
        onConfirm(toJS(fromState(context)));
      },
      children: [/*#__PURE__*/_jsxs(WizardStep, {
        name: "assignment-mode",
        isLastStep: assignmentMode !== ASSIGNMENT_MODES.SPECIFIC,
        children: [selectedAssetsHeader, /*#__PURE__*/_jsx(AssignmentModeControl, {
          assetType: assetType,
          assetCount: assetCount
        })]
      }), /*#__PURE__*/_jsxs(WizardStep, {
        name: "entity-selection",
        children: [selectedAssetsHeader, /*#__PURE__*/_jsx(SpecificPartitioningControl, {})]
      })]
    })
  });
};
ManageAccessDialog.propTypes = {
  actionStatus: ActionStatusesPropType,
  appUsageTracker: PropTypes.object.isRequired,
  assetCount: PropTypes.number,
  assetName: PropTypes.string,
  assetSpecificTrackingProps: PropTypes.object,
  assetType: AssetTypePropType.isRequired,
  onConfirm: PropTypes.func.isRequired
};
export default ManageAccessDialog;